import { useForm } from "@refinedev/antd";
import { HttpError, useApiUrl, useCan, useCustomMutation, useNavigation, useOne, useParsed } from "@refinedev/core";
import { PayloadConfigType } from "../intelligence-search/component/crud/save-config-component";
import { IntelligenceSearchV2 } from "./list";
import { CrudActionButton } from "./components/crud";
import { ExplorationType } from "../ab-test-explore/enum/exploration-types.enum";
import { getUserInfo } from "@src/util/get-user-info";
import dayjs from "dayjs";
import { ConfigTextEditor, ModeSave } from "@src/components/intelligence-search/crud-component/modal-save";
import React from "react";
import { PERMISSION_LAYOUT_CHECK } from "@src/components/crud-permission-layout";
import { LayoutWrapper } from "./components/crud/layout-wrapper";
import { Error403 } from "../error/403";
import { PAGE_URL } from "@src/config";

export const IntelligenceSearchV2Edit = () => {
    const { form } = useForm<any, HttpError, any>();
    const { mutateAsync: handleSaveConfig, isLoading: isLoadingSaveConfig } = useCustomMutation();
    const [modalValues, setModalValues] = React.useState<{ title: string; description: string }>({
        title: "",
        description: "",
    });
    const apiUrl = useApiUrl("nestjsx");
    const { id } = useParsed();
    const [isDontHavePermission, setIsDontHavePermission] = React.useState(false);
    const {
        isFetching: isFetchingGetOne,
        isLoading: isLoadingGetOne,
        data,
        refetch,
    } = useOne({
        dataProviderName: "nestjsx",
        resource: "explorations",
        id: id,
        queryOptions: {
            onSuccess: (data) => {
                const revisedDataWithCorrectTimeRange = {
                    ...(data?.data?.config ?? {}),
                    questions: data?.data?.config?.questions?.map((question: any) => ({
                        ...question,
                        time_range: question?.time_range?.map((time: any) => dayjs(time)),
                    })),
                };
                form.setFieldsValue(revisedDataWithCorrectTimeRange);
                setModalValues({
                    title: data?.data?.title ?? "",
                    description: data?.data?.description ?? "",
                });
            },
            onError: (error) => {
                if (error?.response?.status === 403) {
                    setIsDontHavePermission(true);
                }
            },
        },
    });

    const { data: dataCanEdit } = useCan({
        resource: "intelligence_search",
        action: "edit",
        params: {
            productCode: data?.data?.product_code,
        },
        queryOptions: {
            enabled: !!data?.data?.product_code,
        },
    });

    const userInfo = getUserInfo();

    const isOwnerConfig = data?.data?.owner === userInfo?.email;

    const handleSave = async (values: { title: string; description: string }, mode?: ModeSave) => {
        const userInfo = getUserInfo();
        const formValues = form.getFieldsValue();
        const productCode = formValues?.product_code;

        const payload: PayloadConfigType = {
            title: values.title,
            description: values.description,
            owner: userInfo?.email,
            config: formValues,
            type: ExplorationType.INTELLIGENCE_SEARCH_V2,
            product_code: productCode,
        };
        if (mode === "save-as-new") {
            handleSaveConfig({
                url: `${apiUrl}/explorations`,
                method: "post",
                values: payload,
                successNotification: {
                    type: "success",
                    message: "Successfully created",
                    description: "Your explore have been created",
                },
                config: {
                    headers: {
                        "product-code": payload.product_code,
                    },
                },
            }).then((data) => {
                if (data?.data?.id) {
                    const url = `${window.location.origin}/explorations/intelligence-searches/${data?.data?.id}`;
                    if (isOwnerConfig) {
                        window.open(url, "_blank");
                    } else {
                        window.location.href = url;
                    }
                }
            });

            return;
        }

        handleSaveConfig({
            url: `${apiUrl}/explorations/${id}`,
            method: "put",
            values: payload,
            successNotification: {
                type: "success",
                message: "Successfully updated",
                description: "Your explore have been updated",
            },
            config: {
                headers: {
                    "product-code": payload.product_code,
                },
            },
        }).then(() => {
            refetch();
        });
    };

    const loadingGetOne = isLoadingGetOne || isFetchingGetOne;
    const { push } = useNavigation();

    if (isDontHavePermission) {
        return (
            <Error403
                message="You don't have permission to view this page. Please contact our admin for support
"
                onBack={() => {
                    push(PAGE_URL.EXPLORATION.ROOT);
                }}
            />
        );
    }
    return (
        <LayoutWrapper
            headerActions={
                <CrudActionButton
                    form={form}
                    handleSave={handleSave}
                    initConfigProps={data?.data}
                    modalValuesProps={modalValues}
                    isOwnerConfig={isOwnerConfig}
                />
            }
            title={
                <div className={`flex flex-col ${PERMISSION_LAYOUT_CHECK}`}>
                    <div className="flex items-center">
                        <div className="font-bold text-[18px]">{modalValues?.title}</div>
                        <ConfigTextEditor
                            initValues={modalValues}
                            onConfirmValuesChange={(values) => {
                                setModalValues(values);
                            }}
                        />
                    </div>
                    <div className=" text-[14px] text-gray-500">{modalValues?.description}</div>
                </div>
            }
            loading={loadingGetOne || isLoadingSaveConfig}
            isDisabled={!dataCanEdit?.can}
        >
            <IntelligenceSearchV2 form={form} />
        </LayoutWrapper>
    );
};
